import { Notifications, NotificationsOff, VolumeMute, VolumeUp } from '@mui/icons-material'
import { IconButton, IconButtonProps, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { noop } from 'lodash'
import React from 'react'
import DeleteButton from '../../../common/iconButtons/DeleteButton'
import EditButton from '../../../common/iconButtons/EditButton'
import { Mutation } from '../../../react-apollo/Components'
import RestrictedView from '../../auth/RestrictedView'
import { i18n } from '../../index/index'
import {
  deleteChainReminderMutation,
  getAllChainReminders,
  getStationReminders,
  updateChainReminderStationMutation,
} from '../reminders/RemindersQueries'
import css from '../reminders/reminders.css'
import { StationChainReminder } from '../types'

interface Props {
  readonly stationId?: string
  readonly chainReminders: StationChainReminder[]
  readonly onChainReminderEdit?: (chainReminder: StationChainReminder) => void
}

interface RowProps {
  readonly stationId?: string
  readonly chainReminder: StationChainReminder
  readonly onChainReminderEdit?: (chainReminder: StationChainReminder) => void
}

export default function ChainRemindersTable({ stationId, chainReminders, onChainReminderEdit }: Props) {
  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={css.tableHeading}>{i18n.t('admin.reminders.reminder')}</TableCell>
            <TableCell className={css.tableHeading} padding='checkbox'>
              {i18n.t('admin.reminders.time')}
            </TableCell>
            <TableCell className={css.tableHeading} padding='checkbox'>
              {i18n.t('admin.reminders.weekdays')}
            </TableCell>
            <TableCell className={css.tableHeading} />
          </TableRow>
        </TableHead>
        <TableBody>
          {chainReminders.map((chainReminder) => (
            <ChainReminderRow
              key={chainReminder.id}
              stationId={stationId}
              chainReminder={chainReminder}
              onChainReminderEdit={onChainReminderEdit}
            />
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

function ChainReminderRow({ stationId, chainReminder, onChainReminderEdit }: RowProps) {
  const onMuteChange = (onMutate, mute: boolean) => {
    onMutate({
      variables: {
        chainReminderStationId: chainReminder.chainReminderStationId,
        data: { muted: mute, disabled: chainReminder.disabled },
      },
    })
  }
  const onDisabledChange = (onMutate, chainReminderDisabled: boolean) => {
    onMutate({
      variables: {
        chainReminderStationId: chainReminder.chainReminderStationId,
        data: { muted: chainReminder.muted, disabled: chainReminderDisabled },
      },
    })
  }
  const onDelete = (onMutate, chainReminderId) => {
    onMutate({
      variables: {
        id: chainReminderId,
      },
    })
  }

  return (
    <TableRow>
      <TableCell className={css.tableCell}>{chainReminder.name}</TableCell>
      <TableCell className={css.tableCell}>{chainReminder.time}</TableCell>
      <TableCell className={css.tableCell}>
        {chainReminder.weekdays.length === 7 && i18n.t('admin.reminders.all')}
        {chainReminder.weekdays.length < 7 &&
          chainReminder.weekdays.map((weekday) => i18n.t(`admin.reminders.short.${weekday}`)).join(', ')}
      </TableCell>
      <TableCell className={css.tableCell} align='right'>
        {onChainReminderEdit && (
          <RestrictedView requiredPermission='manage_chain'>
            <EditButton
              className={css.actionButton}
              onClick={() => (chainReminder.id ? onChainReminderEdit(chainReminder) : noop)}
            />
          </RestrictedView>
        )}
        {stationId && chainReminder.chainReminderStationId && (
          <RestrictedView requiredPermission='manage_ownStation'>
            <Mutation
              mutation={updateChainReminderStationMutation}
              refetchQueries={[
                {
                  query: getStationReminders,
                  variables: { stationId },
                },
              ]}
            >
              {(onMutate, { loading }) => (
                <MuteButton
                  muted={chainReminder.muted}
                  onClick={() => onMuteChange(onMutate, !chainReminder.muted)}
                  disabled={loading}
                />
              )}
            </Mutation>
          </RestrictedView>
        )}
        {stationId && chainReminder.chainReminderStationId && (
          <RestrictedView requiredPermission='manage_ownStation'>
            <Mutation
              mutation={updateChainReminderStationMutation}
              refetchQueries={[{ query: getStationReminders, variables: { stationId } }]}
            >
              {(onMutate, { loading }) => (
                <DisableForStationButton
                  chainReminderDisabled={chainReminder.disabled}
                  onClick={() => onDisabledChange(onMutate, !chainReminder.disabled)}
                  disabled={loading}
                />
              )}
            </Mutation>
          </RestrictedView>
        )}
        {!stationId && chainReminder.id && (
          <RestrictedView requiredPermission='manage_chain'>
            <Mutation mutation={deleteChainReminderMutation} refetchQueries={[{ query: getAllChainReminders }]}>
              {(onMutate, { loading }) => (
                <DeleteButton disabled={loading} onClick={() => onDelete(onMutate, chainReminder.id)} />
              )}
            </Mutation>
          </RestrictedView>
        )}
      </TableCell>
    </TableRow>
  )
}

interface MuteButtonProps extends IconButtonProps {
  muted: boolean
}

function MuteButton({ muted, ...rest }: MuteButtonProps) {
  const icon = muted ? <VolumeMute /> : <VolumeUp />
  return <IconButton {...rest}>{icon}</IconButton>
}

interface DisableButtonProps extends IconButtonProps {
  chainReminderDisabled: boolean
}

function DisableForStationButton({ chainReminderDisabled, ...rest }: DisableButtonProps) {
  const icon = chainReminderDisabled ? <NotificationsOff /> : <Notifications />
  return <IconButton {...rest}>{icon}</IconButton>
}
